import Looks3Icon from '@mui/icons-material/Looks3';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import { Box } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { ClaimStage } from '../../../interfaces/billing';
import { BillingProcess } from '../../../interfaces/billing.rpm';
import { IClinic } from '../../../interfaces/clinic';
import { CustomTabs } from '../../common/CustomTabs/CustomTabs';
import { TabPanel } from '../../common/TabPanel/TabPanel';
import { RPMExportResults } from '../export-history/RPMExportResults';
import { PatientSelection } from '../patient-selection/PatientSelection';
import { RPMCalculation } from '../rpm-calculation/RPMCalculation';
import { ActionableTab } from './types';
import { ProcessStatusHeader } from './ProcessStatusHeader';
import styles from './ClaimSummary.module.css';

type ClaimSummaryProps = ActionableTab & {
  process?: BillingProcess | null;
  clinics: IClinic[];
};

function getInitialTabIndexFromProcessStatus(process?: BillingProcess | null) {
  const initialTabIndexByProcessStatus = {
    [ClaimStage.CreationInProgress]: 0, // Stay on Patient Selection during creation
    [ClaimStage.Created]: 1, // Move to RPM Calculation when created
    [ClaimStage.ExportInProgress]: 1, // Stay on RPM Calculation during export
    [ClaimStage.Exported]: 2, // Move to RPM Export Results when exported
    [ClaimStage.Discarded]: 0, // Back to Patient Selection if discarded
  };

  if (!process?.status || !initialTabIndexByProcessStatus[process.status]) {
    return 0;
  }

  return initialTabIndexByProcessStatus[process.status];
}

function getEnabledTabs(process?: BillingProcess | null): number[] {
  if (!process?.status) {
    return [0]; // Only Patient Selection enabled for new process
  }

  switch (process.status) {
    case ClaimStage.CreationInProgress:
      return [0]; // Only Patient Selection accessible during creation
    case ClaimStage.Created:
    case ClaimStage.ExportInProgress:
      return [1]; // Only RPM Calculation accessible when created
    case ClaimStage.Exported:
      return [2]; // Only RPM Export Results accessible when exported
    case ClaimStage.Discarded:
      return [0]; // Only Patient Selection accessible after discard
    default:
      return [0];
  }
}

export const ClaimSummary: FC<ClaimSummaryProps> = ({ onAfterAction, process, clinics }) => {
  const [tabIndex, setTabIndex] = useState(0);

  // Use the passed process directly (updated by global polling)
  const currentProcess = process;
  const enabledTabs = getEnabledTabs(currentProcess);

  useEffect(() => {
    setTabIndex(getInitialTabIndexFromProcessStatus(currentProcess));
  }, [currentProcess]);

  const handleTabChange = (newIndex: number) => {
    // Only allow navigation to enabled tabs
    if (enabledTabs.includes(newIndex)) {
      setTabIndex(newIndex);
    }
  };

  return (
    <Box className={styles.container}>
      {/* Process Status Header */}
      <Box className={styles.header}>
        <Box className={styles.headerBox}>
          <ProcessStatusHeader
            process={currentProcess}
            clinics={clinics}
            onRefresh={onAfterAction}
          />

          {/* Tabs */}
          <Box className={styles.tabsContainer}>
            <CustomTabs
              tabLabels={[
                { label: 'Patient Selection', icon: <LooksOneIcon /> },
                { label: 'RPM Calculation', icon: <LooksTwoIcon /> },
                { label: 'RPM Export Results', icon: <Looks3Icon /> },
              ]}
              value={tabIndex}
              onChange={(_, newIndex) => handleTabChange(newIndex)}
              enabledTabs={enabledTabs}
              aria-label='RPM Process Tabs'
              useEnabledTabs={true}
            />
          </Box>
        </Box>
      </Box>
      
      <TabPanel value={tabIndex} index={0}>
        <PatientSelection 
          clinics={clinics} 
          onAfterAction={onAfterAction}
          currentProcess={currentProcess}
        />
      </TabPanel>
      <TabPanel value={tabIndex} index={1}>
        <RPMCalculation 
          clinics={clinics} 
          onAfterAction={onAfterAction} 
          processId={currentProcess?.id}
        />
      </TabPanel>
      <TabPanel value={tabIndex} index={2}>
        <RPMExportResults 
          clinics={clinics} 
          processId={currentProcess?.id}
        />
      </TabPanel>
    </Box>
  );
};
